// O CONTEÚDO VIRA MARCAÇÃO. É isto que faz o tenant escrever um banner sem escrever HTML.
//
// Sem DOM, sem `window`: entra conteúdo, sai string. Roda em navegador, em Node e no servidor de
// quem renderiza do lado de lá — a mesma costura do `resolve.js`.
//
// ## Por que este módulo existe
//
// O formato de conteúdo do produto declara nove tipos (`html`, `text`, `banner`, `modal`, `toast`,
// `card`, `embed`, `redirect`, `custom`), um objeto de ESTILO com cor, posição, sombra e animação, e
// uma CTA com rótulo, destino e estilo. Medido em 22/08/2026: o resolvedor devolvia `style` e
// NINGUÉM o consumia; o SDK aplicava `innerHTML` e só. Ou seja, oito dos nove tipos e o objeto de
// estilo inteiro eram declaração inerte.
//
// Oferecer um seletor de cor numa tela de autoria enquanto isso é verdade seria a doença que este
// produto persegue: controle que existe, é preenchido, e não muda nada. Este módulo é o consumidor
// que faltava — e é ele que autoriza a tela a oferecer o formato.
//
// ## O que ele NÃO faz
//
//     sem texto e sem html ..... devolve `null`, e quem chama não substitui nada
//     tipo desconhecido ........ cai no texto simples, nunca inventa marcação
//     `custom` ................. devolve `null` de propósito: é dado para a APLICAÇÃO renderizar
//
// ## Segurança
//
// A saída daqui é sanitizada por quem aplica (`sanitizeHtml`), como já era. Os valores de estilo são
// ESCAPADOS aqui mesmo antes de entrar em atributo, porque uma cor vinda de conteúdo é texto de
// terceiro e `style="{cor}"` é injeção esperando acontecer.
//
// ## DADO DO SUJEITO NO TEXTO
//
// Até 22/08/2026 o conteúdo só trocava texto fixo por texto fixo: nenhuma interpolação existia no
// caminho da personalização, e `{{cliente}}` saía literal na tela do visitante. `render_template`
// existia só no motor de REGRAS, nunca aqui.
//
// A interpolação mora NESTE módulo porque ele é o consumidor único e roda nos dois transportes
// (WebSocket e HTTP). Um segundo lugar interpolando faria a mesma frase sair diferente conforme o
// caminho, que é o defeito que este arquivo já existe para impedir.
//
//     o que se pode usar .. as MESMAS chaves que a Política oferece; nada de vocabulário novo
//     ausência ............ ver `_montar`: sem valor e sem padrão declarado, NADA é entregue
//     segurança ........... o valor entra JÁ ESCAPADO, sempre, inclusive no tipo `html`

/** Os tipos que este módulo sabe transformar em marcação. A tela de autoria oferece ESTES. */
export const TIPOS_RENDERIZAVEIS = Object.freeze(["text", "html", "banner", "card", "toast", "modal"]);

/**
 * Uma chave de dado do sujeito no texto: `{{chave}}`, ou `{{chave|padrão}}` quando o autor declara
 * o que mostrar na falta dela.
 *
 * O delimitador é o do motor de regras (`{{ }}`), de propósito: dois delimitadores para a mesma
 * ideia seriam dois idiomas no mesmo produto. O que NÃO vem junto é a EXPRESSÃO. Aqui cabe uma
 * chave, sem filtro, sem aritmética e sem acesso aninhado, porque isto roda no navegador do
 * visitante e um avaliador de expressão ali é superfície de ataque sem contrapartida.
 */
const MARCA = /\{\{\s*([^{}|]+?)\s*(?:\|\s*([^{}]*?)\s*)?\}\}/g;

/** Escapa para texto dentro de HTML. */
function esc(v) {
  return String(v == null ? "" : v)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/** Valor de CSS vindo de conteúdo de terceiro: só o que não fecha o atributo nem abre outro. */
function css(v) {
  const s = String(v == null ? "" : v).trim();
  return /^[#a-zA-Z0-9 ,.()%_-]{0,64}$/.test(s) ? s : "";
}

const SOMBRAS = { none: "none", sm: "0 1px 2px rgba(0,0,0,.06)", md: "0 4px 12px rgba(0,0,0,.10)",
                  lg: "0 10px 24px rgba(0,0,0,.14)", xl: "0 20px 48px rgba(0,0,0,.18)" };

/** O `style=""` do bloco, montado só com o que foi declarado. */
function estilo(s) {
  const e = s || {};
  const partes = [];
  const put = (prop, valor) => { const v = css(valor); if (v) partes.push(`${prop}:${v}`); };
  put("background", e.backgroundColor);
  put("color", e.textColor);
  put("width", e.width);
  put("max-width", e.maxWidth);
  put("height", e.height);
  put("padding", e.padding);
  if (css(e.borderColor)) partes.push(`border:1px solid ${css(e.borderColor)}`);
  if (e.rounded) partes.push("border-radius:10px");
  if (e.shadow && SOMBRAS[e.shadow]) partes.push(`box-shadow:${SOMBRAS[e.shadow]}`);
  return partes.join(";");
}

/** O texto principal. Mesma ordem do `textoDoSlot`, num lugar só. */
function titulo(c) {
  return c.title || c.text || c.body || "";
}

/**
 * O valor de uma chave em forma de TEXTO, ou `null` quando não há o que afirmar.
 *
 * `null`, ausente, `NaN`, e o que não é escalar não têm texto. String só de espaço também não: a
 * frase que a receber fica com um buraco no meio, que é o precedente do template de WhatsApp
 * (variável vazia virando um espaço no meio da frase). `0` e `false` TÊM texto e são valores
 * legítimos, e trocá-los por ausência seria a doença oposta, a que este produto persegue no dado.
 */
function valorDe(dados, chave) {
  const v = dados && typeof dados === "object" ? dados[chave] : undefined;
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return isFinite(v) ? String(v) : null;
  if (typeof v === "boolean") return String(v);
  if (typeof v === "string") return v.trim() ? v : null;
  return null;                                     // objeto e lista não são texto de frase
}

/**
 * Troca as chaves pelo dado do sujeito. O que não tiver valor nem padrão declarado vira LACUNA, e
 * quem chama decide o que fazer com ela (aqui: não entregar nada).
 *
 * A ORDEM É A SEGURANÇA, e é o risco mais alto deste módulo. O texto do AUTOR já vem tratado (o do
 * tipo `html` é marcação por decisão dele; o resto vem escapado), e o valor entra escapado NA
 * SUBSTITUIÇÃO. Interpolar antes do tratamento faria o escape do autor passar por cima do valor num
 * caso e por baixo no outro, e no `html` o valor entraria cru no site de quem nos hospeda.
 *
 * A substituição usa FUNÇÃO, nunca string: num replace com string, `$&` e `$'` dentro do valor
 * seriam interpretados, e o valor é dado de terceiro. E porque a função não é reescaneada, um valor
 * que por acaso contenha `{{outra}}` é texto, não uma chave nova.
 */
function interpolar(texto, dados, lacunas) {
  const s = String(texto);
  if (s.indexOf("{{") < 0) return s;               // sem chave, o texto não é sequer tocado
  return s.replace(MARCA, (_todo, bruta, padrao) => {
    const chave = String(bruta).trim();
    const v = valorDe(dados, chave);
    if (v !== null) return esc(v);
    // O PADRÃO É DECLARAÇÃO DO AUTOR, e é ele que autoriza a frase a sair sem o dado. Vazio não
    // conta como declaração: seria a lacuna escrita de propósito, e o produto não oferece controle
    // cujo efeito é um buraco na frase. Ele já vem tratado junto com o texto que o cerca.
    const p = padrao == null ? "" : String(padrao).trim();
    if (p) return p;
    lacunas.push(chave);
    return "";                                     // irrelevante: com lacuna nada é entregue
  });
}

function botao(cta, s, poe) {
  if (!cta || !cta.label) return "";
  const cor = css((s || {}).accentColor) || "#111";
  // O DESTINO NÃO ACEITA CHAVE, de propósito: URL tem outro contexto de escape (percent-encoding),
  // e o `esc` daqui é de HTML. Uma chave no `href` passaria pela lista de esquemas permitidos ANTES
  // de virar valor, ou seja, a conferência olharia uma coisa e o navegador abriria outra.
  //
  // E o NOME DO CLIQUE no relatório também não: ele é a identidade do botão, não a frase que a
  // pessoa lê. Trocar a chave pelo valor ali faria o mesmo botão virar uma linha por visitante, que
  // é exatamente o problema que o campo "nome deste clique" existe para resolver.
  const destino = /^(https?:|\/|#)/i.test(String(cta.url || "")) ? String(cta.url) : "";
  const attrs = destino ? ` href="${esc(destino)}"` : "";
  const tag = destino ? "a" : "button";
  return `<${tag}${attrs} data-synqi3-cta="${esc(cta.action || cta.label)}"` +
         ` style="display:inline-block;margin-top:10px;padding:8px 14px;border:0;border-radius:8px;` +
         `background:${cor};color:#fff;text-decoration:none;cursor:pointer">${poe(esc(cta.label))}</${tag}>`;
}

/**
 * As chaves de dado do sujeito que um conteúdo cita. Vazio quando não cita nenhuma.
 *
 * Existe para quem precisa saber ANTES de renderizar: a tela de autoria, que confere se a chave
 * está no vocabulário do tenant e monta a prévia, e o resolvedor, que manda para a superfície só o
 * que o conteúdo cita. O varredor do lado do servidor mora em `_common/personalizacao.py`, e a
 * assimetria é de propósito: lá se decide o que PODE viajar, aqui se decide o que é AFIRMADO.
 */
export function chavesDoConteudo(conteudo) {
  const fora = new Set();
  const olhar = (v) => {
    if (typeof v === "string") {
      for (const m of v.matchAll(MARCA)) {
        const k = String(m[1]).trim();
        if (k) fora.add(k);
      }
    } else if (Array.isArray(v)) {
      v.forEach(olhar);
    } else if (v && typeof v === "object") {
      Object.values(v).forEach(olhar);
    }
  };
  olhar(conteudo);
  return Array.from(fora);
}

/**
 * Conteúdo resolvido -> marcação, ou `null` quando não há o que mostrar.
 *
 * ## AUSÊNCIA DE DADO NÃO É ENTREGA PARCIAL
 *
 * Chave citada sem valor e sem padrão declarado faz este módulo devolver `null`, e `null` já
 * significa, em todo o SDK, "não substitui nada": a página fica com o texto que ela já tinha, a
 * impressão não é ecoada e o lugar não conta como personalizado. Ninguém vê meia frase.
 *
 * As duas saídas cabíveis eram essa e "usar um padrão declarado pelo autor ao lado da chave", e
 * elas não competem: o PADRÃO é a autorização, e a falta dele é a falta de autorização. É a mesma
 * forma do `validity` no `/decide` (política que não declara prazo não ganha decisão que viaja) e a
 * mesma do `render_com_lacunas` no motor de regras (mensagem com lacuna não vira notificação).
 * Escolher só o padrão obrigaria todo autor a escrever um, e o esquecido viraria vazio; escolher só
 * a recusa tiraria dele a saída legítima de dizer "sem o nome, diga apenas isto".
 *
 * O precedente que fecha a terceira porta está anotado: no template de WhatsApp a variável vazia
 * virou um espaço no meio da frase, e a mensagem passou em toda validação de "tem texto".
 *
 * @param {{content?: object, style?: object, features?: object}} dado  um slot do `resolveSlots`
 * @returns {string|null}
 */
export function comoHtml(dado) {
  const lacunas = [];
  const html = _montar(dado, lacunas);
  return lacunas.length ? null : html;
}

function _montar(dado, lacunas) {
  const c = (dado && dado.content) || {};
  const s = (dado && dado.style) || {};
  // Sem `features`, toda chave é lacuna, e é isso mesmo: não saber o dado do sujeito não é ter o
  // dado. Conteúdo escrito sem chave nenhuma não passa nem perto disto.
  const poe = (txt) => interpolar(txt, (dado && dado.features) || null, lacunas);
  const tipo = String(c.type || c.content_type || "").toLowerCase();

  // `custom` é dado para a aplicação decidir o que fazer. Renderizar aqui seria inventar uma forma
  // que o autor não escolheu.
  if (tipo === "custom") return null;
  // No `html` a marcação é do autor e não é escapada, como sempre foi. O VALOR continua sendo, e é
  // aqui que interpolar antes do escape abriria injeção no site de quem nos hospeda.
  if (tipo === "html" || (!tipo && c.html)) return c.html ? poe(String(c.html)) : null;

  const t = titulo(c);
  const corpo = c.body && c.body !== t ? String(c.body) : "";
  if (!t && !corpo && !c.html) return null;          // sem texto não substitui nada

  // `text` é o caso simples e o padrão: devolve o texto, sem embrulho. Embrulhar sempre quebraria a
  // marcação da página de quem só quer trocar uma frase.
  if (!tipo || tipo === "text") return poe(esc(t));

  const st = estilo(s);
  const abre = `<div data-synqi3-tipo="${esc(tipo)}"${st ? ` style="${esc(st)}"` : ""}>`;
  const cabeca = t ? `<div style="font-weight:600">${poe(esc(t))}</div>` : "";
  const texto = corpo ? `<div style="margin-top:4px">${poe(esc(corpo))}</div>` : "";
  const img = css(c.image_url) || (/^https?:\/\//i.test(String(c.image || "")) ? String(c.image) : "");
  const figura = img ? `<img src="${esc(img)}" alt="" style="max-width:100%;border-radius:8px">` : "";

  if (TIPOS_RENDERIZAVEIS.includes(tipo)) {
    return `${abre}${figura}${cabeca}${texto}${botao(c.cta, s, poe)}</div>`;
  }
  // Tipo declarado que este módulo não sabe montar: o texto vai, a forma não é inventada.
  return poe(esc(t));
}
